import { useMemo, type RefObject } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, ELEVATION, Z_INDEX } from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { computeFeatureLabels } from '../../utils/planFeatureLabels';
import { DialogPortal } from '../common/DialogPortal';
import { DEFENSE_STRATEGIES, strategyBadgeColors } from './defenseStrategies';

/**
 * The card that follows the pointer while a defense row is dragged in the
 * plan list (usePlanListDrag.web.ts). Line 1 names the dragged defense (and
 * "+N" for the rest of a set); line 2 says what a drop here would do, or
 * why it can't. It reads the uiStore `planListDrag` slice and renders
 * nothing outside a drag. Portaled like Popover so no panel clips it; the
 * hook moves it by writing `transform` on the node, never through state.
 */
export function PlanDragGhost({ nodeRef }: { nodeRef: RefObject<View | null> }) {
  const { colors, isDark } = useTheme();
  const drag = useUIStore((s) => s.planListDrag);
  const features = useDefensePlansStore((s) => s.ownPlan?.features);
  const labels = useMemo(
    () => computeFeatureLabels(features ?? [], (s) => DEFENSE_STRATEGIES[s].label),
    [features]
  );
  if (!drag) return null;
  const first = features?.find((f) => f.id === drag.featureIds[0]);
  if (!first) return null;
  const def = DEFENSE_STRATEGIES[first.strategy];
  const badge = strategyBadgeColors(first.strategy, isDark);
  const label = labels[first.id] ?? def.label;
  const extra = drag.featureIds.length - 1;
  const outcome = drag.outcome;
  return (
    <DialogPortal>
      <View
        ref={nodeRef}
        pointerEvents="none"
        testID="plan-drag-ghost"
        style={[
          styles.ghost,
          ELEVATION.soft,
          {
            backgroundColor: colors.surfaceElevated,
            borderColor: colors.border,
            zIndex: Z_INDEX.popover,
          },
        ]}>
        <View style={styles.titleRow}>
          <View style={[styles.badge, { backgroundColor: badge.tint }]}>
            <def.icon size={12} color={badge.icon} />
          </View>
          <Text style={[styles.label, { color: colors.text }]} numberOfLines={1}>
            {label}
          </Text>
          {extra > 0 && (
            <Text style={[styles.more, { color: colors.textSecondary }]}>{`+${extra}`}</Text>
          )}
        </View>
        {outcome && (
          <Text
            style={[
              styles.outcome,
              { color: outcome.disabled ? colors.danger : colors.textSecondary },
            ]}
            numberOfLines={1}>
            {outcome.label}
          </Text>
        )}
      </View>
    </DialogPortal>
  );
}

const styles = StyleSheet.create({
  // Parked at the viewport's origin and hidden until the hook's first frame
  // writes a transform and opacity (the node is fixed, so it never lays out
  // inside the panel).
  ghost: {
    position: 'fixed' as 'absolute',
    top: 0,
    left: 0,
    opacity: 0,
    maxWidth: 280,
    minWidth: 160,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.xs,
    gap: SPACING.xxs,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  badge: {
    width: 20,
    height: 20,
    borderRadius: BORDER_RADIUS.full,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  label: {
    ...TYPOGRAPHY.label,
    flexShrink: 1,
    minWidth: 0,
  },
  more: {
    ...TYPOGRAPHY.caption,
  },
  outcome: {
    ...TYPOGRAPHY.caption,
  },
});
